import React, { useState, useContext, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import toast from "react-hot-toast";

function AddPaymentMethodCheckout() {
  const { userId: contextUserId, accessToken: contextAccessToken } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const canceled = searchParams.get('canceled');
  const recoveryToken = searchParams.get('recovery_token');
  const recoveryEmail = searchParams.get('email');

  // Get userId and accessToken from localStorage or context
  const getUserData = () => {
    const storedUserId = localStorage.getItem('user_id') || localStorage.getItem('userId');
    const storedToken = localStorage.getItem('access_token') || localStorage.getItem('token');
    return {
      userId: contextUserId || storedUserId,
      accessToken: contextAccessToken || storedToken
    };
  };

  useEffect(() => {
    if (canceled === 'true') {
      toast.error("Payment method setup was canceled");
    }
  }, [canceled]);

  useEffect(() => {
    // Check if this is a recovery flow (user coming from app without payment method)
    if (recoveryToken && recoveryEmail) {
      console.log("Recovery flow detected - user needs to add payment method");
      handleRecoveryCheckout(recoveryToken, recoveryEmail);
      return;
    }

    // Normal flow - user is logged in
    const { userId, accessToken } = getUserData();
    
    if (!userId || !accessToken) {
      console.log("No credentials found, redirecting to login");
      navigate("/login");
      return;
    }

    // Auto-redirect to Stripe Checkout
    handleRedirectToCheckout(userId, accessToken);
  }, [contextUserId, contextAccessToken, recoveryToken, recoveryEmail]);

  const handleRecoveryCheckout = async (token, email) => {
    setLoading(true);
    
    try {
      console.log("Creating Recovery Checkout Session for email:", email);
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/create_checkout_session_recovery/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ 
            email: email,
            recovery_token: token 
          }),
        }
      );

      const data = await response.json();
      console.log("Recovery Checkout Session response:", data);

      if (!response.ok) {
        if (data.has_payment_method) {
          toast.success("You already have a payment method added!");
          navigate("/login");
          return;
        }
        throw new Error(data.error || "Failed to create checkout session");
      }

      // Redirect to Stripe Checkout
      console.log("Redirecting to Stripe Checkout:", data.checkout_url);
      window.location.href = data.checkout_url;
      
    } catch (error) {
      console.error("Error:", error);
      toast.error(error.message || "Failed to load payment page");
      setLoading(false);
    }
  };

  const handleRedirectToCheckout = async (userId, accessToken) => {
    setLoading(true);
    
    try {
      console.log("Creating Checkout Session for user:", userId);
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/create_checkout_session_payment_method/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ user_id: userId }),
        }
      );

      const data = await response.json();
      console.log("Checkout Session response:", data);

      if (!response.ok) {
        if (data.has_payment_method) {
          toast.success("You already have a payment method added!");
          navigate("/");
          return;
        }
        throw new Error(data.error || "Failed to create checkout session");
      }

      // Redirect to Stripe Checkout
      console.log("Redirecting to Stripe Checkout:", data.checkout_url);
      window.location.href = data.checkout_url;
      
    } catch (error) {
      console.error("Error:", error);
      toast.error(error.message || "Failed to load payment page");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full mx-auto p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Redirecting to Payment Setup
          </h2>
          <p className="text-gray-600 mb-4">
            Please wait while we redirect you to our secure payment page...
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
            <p className="text-sm text-blue-800">
              <strong>No charge today!</strong> We're just saving your card for easy access when our beta program ends.
            </p>
          </div>
          <p className="text-xs text-gray-500 mt-6">
            Secured by Stripe • Your information is encrypted and safe
          </p>
        </div>
      </div>
    </div>
  );
}

export default AddPaymentMethodCheckout;

